import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface TypingState {
  user_id: string;
  full_name: string;
  typing: boolean;
  updated_at: number;
}

interface PresenceMeta extends TypingState {
  presence_ref: string;
}

const IDLE_MS = 3500;

/**
 * Per-room typing presence using Supabase Realtime Presence.
 * - `typingUsers`: other members currently typing
 * - `setTyping(true)` on each keystroke; auto-clears after IDLE_MS
 * - `setTyping(false)` to stop immediately (e.g., after sending)
 */
export function useChatTyping({
  roomId,
  userId,
  fullName,
}: {
  roomId: string | null | undefined;
  userId: string | null | undefined;
  fullName: string | null | undefined;
}) {
  const [typingMap, setTypingMap] = useState<Record<string, TypingState>>({});
  const channelRef = useRef<RealtimeChannel | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentTypingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!roomId || !userId) return;

    const channel = supabase.channel(`chat-typing-${roomId}`, {
      config: { presence: { key: userId } },
    });

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState() as Record<string, PresenceMeta[]>;
      const next: Record<string, TypingState> = {};
      for (const [uid, metas] of Object.entries(state)) {
        if (uid === userId) continue;
        const latest = metas[metas.length - 1];
        if (latest?.typing) {
          next[uid] = {
            user_id: latest.user_id,
            full_name: latest.full_name,
            typing: true,
            updated_at: latest.updated_at ?? Date.now(),
          };
        }
      }
      setTypingMap(next);
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel.track({
          user_id: userId,
          full_name: fullName ?? "",
          typing: false,
          updated_at: Date.now(),
        });
      }
    });

    channelRef.current = channel;

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      supabase.removeChannel(channel);
      channelRef.current = null;
      lastSentTypingRef.current = false;
      setTypingMap({});
    };
  }, [roomId, userId, fullName]);

  const sendTyping = useCallback(
    (typing: boolean) => {
      const ch = channelRef.current;
      if (!ch || !userId) return;
      lastSentTypingRef.current = typing;
      ch.track({
        user_id: userId,
        full_name: fullName ?? "",
        typing,
        updated_at: Date.now(),
      });
    },
    [userId, fullName],
  );

  const setTyping = useCallback(
    (typing: boolean) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (typing) {
        if (!lastSentTypingRef.current) sendTyping(true);
        idleTimerRef.current = setTimeout(() => sendTyping(false), IDLE_MS);
      } else {
        if (lastSentTypingRef.current) sendTyping(false);
      }
    },
    [sendTyping],
  );

  const typingUsers = useMemo(() => Object.values(typingMap), [typingMap]);

  return { typingUsers, setTyping };
}
